import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/vue-query'
import { queries } from '~/keys'
import type { SuccessResponse } from '~/types/response'
import type { ArticleData, KnowledgeBaseItem } from '~/types/auth'

export const useGetUserManuals = ({
  enabled,
}: {
  enabled?: boolean
} = {}) => {
  const { fetch } = useAPIFetch()
  return useQuery({
    ...queries.userManuals.all,
    queryFn: () =>
      fetch<SuccessResponse<Record<string, any>[]>>({
        method: 'GET',
        url: `/catalogue/user-manuals`,
      }),
    enabled,
    placeholderData: keepPreviousData,
    select: data => data.data,
  })
}

export const useGetUserManual = ({ id, enabled }: { id: ArticleData['id']; enabled?: boolean }) => {
  const { fetch } = useAPIFetch()

  return useQuery({
    ...queries.userManuals.details(id),
    queryFn: () =>
      fetch<SuccessResponse<ArticleData>>({
        method: 'GET',
        url: `/catalogue/user-manuals/${id}`,
      }),
    select: data => data.data.data,
    enabled,
  })
}

export const useGetUsefulMaterials = ({
  enabled,
}: {
  enabled?: boolean
} = {}) => {
  const { fetch } = useAPIFetch()
  return useQuery({
    ...queries.usefulMaterials.all,
    queryFn: () =>
      fetch<SuccessResponse<Record<string, any>[]>>({
        method: 'GET',
        url: `catalogue/useful-materials`,
      }),
    enabled,
    placeholderData: keepPreviousData,
    select: data => data.data,
  })
}

export const useGetUsefulMaterial = ({ id, enabled }: { id: ArticleData['id']; enabled?: boolean }) => {
  const { fetch } = useAPIFetch()

  return useQuery({
    ...queries.usefulMaterials.details(id),
    queryFn: () =>
      fetch<SuccessResponse<ArticleData[]>>({
        method: 'GET',
        url: `catalogue/useful-materials/${id}`,
      }),
    select: data => data.data.data,
    enabled,
  })
}

export const useGetRecentlyViewedKnowledgeBase = ({
  enabled,
}: {
  enabled?: boolean
} = {}) => {
  const { fetch } = useAPIFetch()
  return useQuery({
    ...queries.knowledgeBase.recentlyViewed,
    queryFn: () =>
      fetch<SuccessResponse<KnowledgeBaseItem[]>>({
        method: 'GET',
        url: `/knowledge-base/recently-viewed`,
      }),
    enabled,
    select: data => data.data,
  })
}

export const useGetPopularKnowledgeBase = ({
  enabled,
}: {
  enabled?: boolean
} = {}) => {
  const { fetch } = useAPIFetch()
  return useQuery({
    ...queries.knowledgeBase.popular,
    queryFn: () =>
      fetch<SuccessResponse<KnowledgeBaseItem[]>>({
        method: 'GET',
        url: `/knowledge-base/popular`,
      }),
    enabled,
    select: data => data.data,
  })
}

export const useGetLastUpdatedKnowledgeBase = ({
  query,
  queryKey,
  enabled,
}: {
  query?: MaybeRef<any>
  queryKey?: MaybeRef<any>
  enabled?: Ref<boolean>
} = {}) => {
  const { fetch } = useAPIFetch()
  return useInfiniteQuery({
    ...queries.knowledgeBase.lastUpdated(queryKey),
    queryFn: ({ pageParam = 1 }) =>
      fetch<SuccessResponse<KnowledgeBaseItem[]>>({
        method: 'GET',
        url: `/knowledge-base/last-updated`,
        params: {
          ...query?.value,
          page: pageParam,
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const total = pages.reduce((acc, page) => acc + page.data.data.length, 0)
      return total < lastPage.data.pagination.total ? pages.length + 1 : undefined
    },
    placeholderData: keepPreviousData,
    retry: false,
    select: data => data,
    enabled,
  })
}

export const useGetSearchKnowledgeBase = ({
  query,
  queryKey,
  enabled,
}: {
  query?: MaybeRef<any>
  queryKey?: MaybeRef<any>
  enabled?: Ref<boolean>
} = {}) => {
  const { fetch } = useAPIFetch()
  return useInfiniteQuery({
    ...queries.knowledgeBase.allPaginated(queryKey),
    queryFn: ({ pageParam = 1 }) =>
      fetch<SuccessResponse<KnowledgeBaseItem[]>>({
        method: 'GET',
        url: `/knowledge-base/search`,
        params: {
          ...query?.value,
          page: pageParam,
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const total = pages.reduce((acc, page) => acc + page.data.data.length, 0)
      return total < lastPage.data.pagination.total ? pages.length + 1 : undefined
    },
    placeholderData: keepPreviousData,
    retry: false,
    select: data => data,
    enabled,
  })
}
